import { useCallback, useEffect, useRef, useState } from 'react';
import { Info, TriangleAlert, XCircle, CheckCircle2, X } from 'lucide-react';
import { getSinaState } from './sinaStates.js';
import { subscribeToSinaNotifications } from './sinaNotifications.js';
import './SinaNotificationHost.css';

const EXIT_DURATION = 230;
const DEFAULT_DURATION = 3500;
const TYPEWRITER_CHAR_MS = 24;
const icons = { success: CheckCircle2, warning: TriangleAlert, error: XCircle, info: Info };
const getSinaStateKey = (notification) => notification.avatarState || notification.type;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Escribe el mensaje letra por letra, como si Sina estuviera hablando.
function useTypewriter(text, enabled) {
  const [visibleLength, setVisibleLength] = useState(enabled ? 0 : text.length);

  useEffect(() => {
    if (!enabled || prefersReducedMotion()) {
      setVisibleLength(text.length);
      return undefined;
    }
    setVisibleLength(0);
    const interval = window.setInterval(() => {
      setVisibleLength((length) => {
        if (length >= text.length) {
          window.clearInterval(interval);
          return length;
        }
        return length + 1;
      });
    }, TYPEWRITER_CHAR_MS);
    return () => window.clearInterval(interval);
  }, [text, enabled]);

  return text.slice(0, visibleLength);
}

function NotificationMessage({ text, typewriter }) {
  const visibleText = useTypewriter(text, typewriter);
  if (!typewriter) return <p id="sina-notification-message">{text}</p>;
  return (
    <p id="sina-notification-message" style={{ display: 'grid' }}>
      <span className="sr-only">{text}</span>
      {/* The invisible full text reserves the final height so the card does not jump while typing. */}
      <span aria-hidden="true" style={{ gridArea: '1 / 1', visibility: 'hidden' }}>{text}</span>
      <span aria-hidden="true" style={{ gridArea: '1 / 1' }}>{visibleText}</span>
    </p>
  );
}

// The bubble belongs to the floating Sina: it reports which face to show while it speaks instead of drawing its own.
export default function SinaNotificationHost({ onSpeakingChange }) {
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const [isLeaving, setIsLeaving] = useState(false);
  const primaryActionRef = useRef(null);

  useEffect(() => subscribeToSinaNotifications((notification) => {
    setQueue((items) => [...items, notification]);
  }), []);

  useEffect(() => {
    if (!current && queue.length) {
      setCurrent(queue[0]);
      setQueue((items) => items.slice(1));
    }
  }, [current, queue]);

  const speakingState = current ? getSinaStateKey(current) : null;
  useEffect(() => {
    onSpeakingChange?.(speakingState);
  }, [speakingState, onSpeakingChange]);

  useEffect(() => {
    if (!current?.blocking) return undefined;
    const frame = window.requestAnimationFrame(() => primaryActionRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [current]);

  const close = useCallback(() => {
    if (!current || isLeaving) return;
    setIsLeaving(true);
    window.setTimeout(() => {
      current.resolve?.();
      setCurrent(null);
      setIsLeaving(false);
    }, EXIT_DURATION);
  }, [current, isLeaving]);

  useEffect(() => {
    if (!current || current.blocking) return undefined;
    const timeout = window.setTimeout(close, current.duration ?? DEFAULT_DURATION);
    return () => window.clearTimeout(timeout);
  }, [current, close]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && current) close();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [current, close]);

  if (!current) return null;

  const Icon = icons[current.type];
  const sinaState = getSinaState(getSinaStateKey(current));
  const primaryAction = current.primaryAction || (current.actionText ? { label: current.actionText } : null);
  const closeAfter = (action) => {
    action?.onClick?.();
    close();
  };

  return (
    <section
      className={`sina-notification sina-notification--${current.type} ${current.typewriter ? 'sina-notification--talking' : ''} ${isLeaving ? 'sina-notification--leaving' : ''}`}
      role={current.blocking ? 'alertdialog' : 'status'}
      aria-live={current.blocking ? undefined : 'polite'}
      aria-labelledby="sina-notification-title"
      aria-describedby="sina-notification-message"
    >
      <div className="sina-notification__card">
        <div className="sina-notification__accent" aria-hidden="true"><Icon size={15} /></div>
        <div className="sina-notification__content">
          <p className="sina-notification__eyebrow">{current.eyebrow || `SINA · ${current.type === 'warning' ? 'Aviso' : sinaState.label}`}</p>
          <h2 id="sina-notification-title">{current.title}</h2>
          {current.message ? <NotificationMessage key={current.id} text={current.message} typewriter={current.typewriter} /> : null}
          {(primaryAction || current.secondaryAction) ? (
            <div className="sina-notification__actions">
              {current.secondaryAction ? <button type="button" className="sina-notification__secondary" onClick={() => closeAfter(current.secondaryAction)}>{current.secondaryAction.label}</button> : null}
              {primaryAction ? <button ref={primaryActionRef} type="button" className="sina-notification__primary" onClick={() => closeAfter(primaryAction)}>{primaryAction.label}</button> : null}
            </div>
          ) : <button ref={primaryActionRef} type="button" className="sina-notification__close-text" onClick={close}>Cerrar</button>}
        </div>
        <button type="button" className="sina-notification__dismiss" onClick={close} aria-label="Cerrar aviso de SINA"><X size={16} /></button>
      </div>
    </section>
  );
}
